import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
// ตัวคุมจำนวนต่อหน้า/เลขหน้าของ repo ที่ใช้ Stack ล้วน — จอนี้ไม่สร้างสำเนาที่ห้า
import CataloguePagination from "../Product/shared/Pagination";
import { numberWithCommas } from "../../utils";
import { useDebounce } from "../../utils/hooks/useDebounce";
import { Branch } from "../../types/user.type";
import {
  PURCHASE_ORDER_IMPORT_PAST_DELIVERY_LABEL,
  PurchaseOrderBranchRef,
  PurchaseOrderImportPreviewRow,
} from "../../types/purchase-order.type";
import PreviewRow from "./PreviewRow";
import BranchChoiceField, { NO_BRANCH_CHOSEN } from "./BranchChoiceField";
import {
  ALL_RESULTS,
  filterPreviewRows,
  isPastImportable,
  normalizePreviewSearch,
  ResultFilterValue,
} from "./branchChoice";
import {
  applyRangeSelection,
  isSelectableRow,
  setRowsSelection,
  summarizeSelection,
} from "./selection";
import {
  BRANCH_REQUIRED_RESULT,
  CARD_CONTENT_SX,
  CARD_SX,
  ROW_FILTER_LABEL,
  ROW_FILTER_OPTIONS,
} from "./constants";

const DEFAULT_LIMIT = 20;

/**
 * ความกว้างของทุกคอลัมน์ · ตารางเป็น fixed layout เพื่อให้คอลัมน์ไม่ขยับเมื่อเนื้อหาในช่อง
 * เปลี่ยน (เช่นติ๊กนำเข้าแล้วช่อง สาขา / ร้าน เปลี่ยนจาก "ไม่นำเข้า" เป็นสาขา + ร้าน) ·
 * สถานที่จัดส่ง ไม่กำหนด จึงรับพื้นที่ที่เหลือ และข้อความยาวถูกตัดด้วย …
 */
const COLUMNS: { label: string; width?: number; align?: "right" }[] = [
  { label: "แถว", width: 64 },
  { label: "เลข PO", width: 120 },
  { label: "วันส่ง", width: 104 },
  { label: "รหัสร้าน", width: 88 },
  { label: "สถานที่จัดส่ง" },
  { label: "จำนวนเงิน", width: 112, align: "right" },
  { label: "ผลตรวจ", width: 148 },
  { label: "สาขา / ร้าน", width: 280 },
];
const CHECKBOX_COLUMN_WIDTH = 48;
const COLUMN_COUNT = COLUMNS.length + 1;

/** ต่ำกว่านี้ fixed layout จะบีบคอลัมน์ สถานที่จัดส่ง จนอ่านไม่ออก ให้เลื่อนแนวนอนแทน */
const TABLE_MIN_WIDTH = 1180;

/** รอให้พิมพ์จบก่อนค่อยกรอง ~8,700 แถว ไม่กรองใหม่ทุกตัวอักษร */
const SEARCH_DEBOUNCE_MS = 300;

/** เวลาที่แจ้ง "เติมสาขาให้อีก N แถว" ค้างอยู่บนจอ */
const AUTO_FILL_NOTE_MS = 5000;

/** ผลของการเติมสาขาให้ร้านเดียวกันครั้งล่าสุด · seq เปลี่ยนทุกครั้งเพื่อให้แจ้งซ้ำได้ */
export interface AutoFillNote {
  count: number;
  seq: number;
}

interface PreviewRowsTableProps {
  rows: PurchaseOrderImportPreviewRow[];
  branches: Branch[];
  branchChoiceByRow: Record<number, string>;
  onChooseBranch: (rowNo: number, companyId: string) => void;
  /** เลือกสาขาเดียวให้หลายแถว — เขียนทับแถวที่เลือก และเติมให้ร้านเดียวกันที่ยังว่าง */
  onBulkChooseBranch: (rowNos: number[], companyId: string) => void;
  autoFillNote: AutoFillNote | null;
  onDismissAutoFillNote: () => void;
  /** แถวเกินกำหนดส่งที่ติ๊กนำเข้า (ค่าตั้งต้นว่าง = ไม่นำเข้า) */
  includedPastRowNos: ReadonlySet<number>;
  onTogglePastRow: (rowNo: number) => void;
  onSetAllPastRows: (include: boolean) => void;
  /** นำเข้า / ไม่นำเข้า ทีละหลายแถว (แถบของชุดที่เลือก) */
  onSetPastRows: (rowNos: number[], include: boolean) => void;
}

/**
 * ตารางตัวอย่างของ W2
 *
 * preview คืนทุกแถวที่อ่านได้ (ไฟล์จริงของบิ๊กซีมี ~8,700 แถว) จอจึงค้นหา กรอง และแบ่งหน้า
 * ฝั่ง client — ไม่มีการเรียก API ซ้ำเพื่อเปลี่ยนมุมมอง และเปิดมาที่ "ต้องเลือกสาขา"
 * ก่อนเสมอเมื่อมีแถวที่ยังค้าง เพราะนั่นคืองานที่ HO ต้องทำในขั้นนี้
 *
 * ติ๊กเลือกได้ทุกแถวที่บันทึกได้ (ไม่ใช่แถวซ้ำ) เก็บเป็น Set ของ row_no ข้ามหน้าและข้าม
 * การค้นหา · Shift+คลิกเลือกเป็นช่วงตามลำดับที่กรองอยู่ · แถบของชุดที่เลือกแสดงเฉพาะปุ่ม
 * ที่มีผลกับชุดนั้น พร้อมจำนวนของแต่ละปุ่ม
 */
const PreviewRowsTable = ({
  rows,
  branches,
  branchChoiceByRow,
  onChooseBranch,
  onBulkChooseBranch,
  autoFillNote,
  onDismissAutoFillNote,
  includedPastRowNos,
  onTogglePastRow,
  onSetAllPastRows,
  onSetPastRows,
}: PreviewRowsTableProps) => {
  const [resultFilter, setResultFilter] = useState<ResultFilterValue>(() =>
    rows.some((row) => row.result === BRANCH_REQUIRED_RESULT)
      ? BRANCH_REQUIRED_RESULT
      : ALL_RESULTS,
  );
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [selectedRowNos, setSelectedRowNos] = useState<Set<number>>(
    () => new Set(),
  );
  const [bulkCompanyId, setBulkCompanyId] = useState<string>(NO_BRANCH_CHOSEN);

  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const normalizedSearch = normalizePreviewSearch(debouncedSearch);

  const filteredRows = useMemo(
    () => filterPreviewRows(rows, resultFilter, normalizedSearch),
    [rows, resultFilter, normalizedSearch],
  );

  const pageRows = useMemo(
    () => filteredRows.slice((page - 1) * limit, page * limit),
    [filteredRows, page, limit],
  );

  // แถวที่เลือกได้ของชุดที่กรองอยู่ ตามลำดับที่แสดง — ฐานของ "เลือกทั้งหมดที่กรองอยู่"
  // (ทุกหน้า ไม่ใช่แค่หน้าที่เห็น) และของ Shift+คลิก
  const filteredSelectableRowNos = useMemo(
    () => filteredRows.filter(isSelectableRow).map((row) => row.row_no),
    [filteredRows],
  );

  const pastImportableCount = useMemo(
    () => rows.filter(isPastImportable).length,
    [rows],
  );
  const allPastIncluded =
    pastImportableCount > 0 && includedPastRowNos.size === pastImportableCount;

  const selectedInFilterCount = useMemo(
    () =>
      filteredSelectableRowNos.filter((rowNo) => selectedRowNos.has(rowNo))
        .length,
    [filteredSelectableRowNos, selectedRowNos],
  );
  const allFilteredSelected =
    filteredSelectableRowNos.length > 0 &&
    selectedInFilterCount === filteredSelectableRowNos.length;

  // ปุ่มของแถบชุดที่เลือก แต่ละปุ่มนับเฉพาะแถวที่ตัวเองมีผล
  const actions = useMemo(
    () => summarizeSelection(rows, selectedRowNos, includedPastRowNos),
    [rows, selectedRowNos, includedPastRowNos],
  );

  // สาขาที่มีร้านถือรหัสของแถว ต้องเลือกสาขา ที่เลือกไว้ เสนอไว้บนสุดของ dropdown
  const bulkCandidates = useMemo<PurchaseOrderBranchRef[]>(() => {
    if (actions.branchRequired.length === 0) return [];
    const branchRequiredSet = new Set(actions.branchRequired);
    const byCompanyId = new Map<string, PurchaseOrderBranchRef>();
    rows.forEach((row) => {
      if (!branchRequiredSet.has(row.row_no)) return;
      row.branch_candidates.forEach((candidate) => {
        if (!byCompanyId.has(candidate.company_id)) {
          byCompanyId.set(candidate.company_id, candidate);
        }
      });
    });
    return Array.from(byCompanyId.values());
  }, [rows, actions.branchRequired]);

  // เปลี่ยนตัวกรองแล้วหน้าที่ค้างอยู่มักเลยท้ายชุดใหม่ ซึ่งอ่านเป็น "ไม่มีข้อมูล"
  useEffect(() => {
    setPage(1);
  }, [resultFilter, normalizedSearch]);

  // ref ของแถวที่คลิกล่าสุดและลำดับที่กรองอยู่ ทำให้ callback ของแถวคงที่ข้าม render —
  // แถวที่ห่อ memo จึงไม่ render ใหม่ทั้งหน้าทุกครั้งที่ติ๊ก
  const anchorRowNoRef = useRef<number | null>(null);
  const orderedRowNosRef = useRef<number[]>(filteredSelectableRowNos);
  orderedRowNosRef.current = filteredSelectableRowNos;

  const handleToggleSelectRow = useCallback(
    (rowNo: number, shiftKey: boolean) => {
      const anchor = shiftKey ? anchorRowNoRef.current : null;
      anchorRowNoRef.current = rowNo;
      setSelectedRowNos((current) =>
        applyRangeSelection(current, orderedRowNosRef.current, anchor, rowNo),
      );
    },
    [],
  );

  const handleChangeResultFilter = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = ROW_FILTER_OPTIONS.find(
      (option) => option === event.target.value,
    );
    setResultFilter(selected ?? ALL_RESULTS);
  };

  const handleChangeSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleToggleSelectAllFiltered = () => {
    setSelectedRowNos((current) =>
      setRowsSelection(current, filteredSelectableRowNos, !allFilteredSelected),
    );
  };

  const handleClearSelection = () => {
    setSelectedRowNos(new Set());
    anchorRowNoRef.current = null;
  };

  // นำเข้า / ไม่นำเข้า ไม่ล้างชุดที่เลือก — แถว ต้องเลือกสาขา ที่เพิ่งนำเข้าต้องเลือกสาขาต่อ
  // จากชุดเดิมได้ทันที ต่างจาก ใช้กับแถวที่เลือก ที่ใช้ชุดนั้นจบแล้ว
  const handleIncludePast = () => {
    onSetPastRows(actions.pastToInclude, true);
  };

  const handleExcludePast = () => {
    onSetPastRows(actions.pastToExclude, false);
  };

  const handleApplyBulk = () => {
    if (
      bulkCompanyId === NO_BRANCH_CHOSEN ||
      actions.branchRequired.length === 0
    ) {
      return;
    }
    onBulkChooseBranch(actions.branchRequired, bulkCompanyId);
    setSelectedRowNos(new Set());
    anchorRowNoRef.current = null;
    setBulkCompanyId(NO_BRANCH_CHOSEN);
  };

  const handleCloseAutoFillNote = (_event: unknown, reason?: string) => {
    // คลิกที่อื่นบนจอ (เช่นเปิด dropdown แถวถัดไป) ไม่ควรปิดข้อความทันที
    if (reason === "clickaway") return;
    onDismissAutoFillNote();
  };

  return (
    <Card sx={CARD_SX}>
      <CardContent sx={CARD_CONTENT_SX}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          mb={1.5}
        >
          <Typography variant="subtitle1">
            แถวในไฟล์ ({numberWithCommas(filteredRows.length)} จาก{" "}
            {numberWithCommas(rows.length)})
          </Typography>

          <Stack direction="row" gap={1.5} flexWrap="wrap">
            <TextField
              size="small"
              label="ค้นหาสถานที่จัดส่ง"
              value={search}
              onChange={handleChangeSearch}
              placeholder="store number / ชื่อ / เลข PO"
              sx={{ minWidth: 260 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              select
              size="small"
              label="ผลตรวจ"
              value={resultFilter}
              onChange={handleChangeResultFilter}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value={ALL_RESULTS}>ทั้งหมด</MenuItem>
              {ROW_FILTER_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {ROW_FILTER_LABEL[option]}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Stack>

        {pastImportableCount > 0 && (
          <FormControlLabel
            sx={{ mb: 1 }}
            control={
              <Checkbox
                size="small"
                checked={allPastIncluded}
                indeterminate={includedPastRowNos.size > 0 && !allPastIncluded}
                onChange={() => onSetAllPastRows(!allPastIncluded)}
              />
            }
            label={`นำเข้าทั้งหมดที่${PURCHASE_ORDER_IMPORT_PAST_DELIVERY_LABEL} (${numberWithCommas(pastImportableCount)} แถว)`}
          />
        )}

        {selectedRowNos.size > 0 && (
          <Paper
            variant="outlined"
            sx={{ p: 1.5, mb: 1.5, bgcolor: "background.default" }}
          >
            <Stack
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              gap={1.5}
            >
              <Typography variant="body2" fontWeight={600}>
                เลือกแล้ว {numberWithCommas(selectedRowNos.size)} แถว
              </Typography>

              {actions.pastToInclude.length > 0 && (
                <Button variant="outlined" size="small" onClick={handleIncludePast}>
                  นำเข้า ({numberWithCommas(actions.pastToInclude.length)})
                </Button>
              )}
              {actions.pastToExclude.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  color="inherit"
                  onClick={handleExcludePast}
                >
                  ไม่นำเข้า ({numberWithCommas(actions.pastToExclude.length)})
                </Button>
              )}

              {actions.branchRequired.length > 0 && (
                <>
                  <Box sx={{ width: 260 }}>
                    <BranchChoiceField
                      candidates={bulkCandidates}
                      branches={branches}
                      value={bulkCompanyId}
                      onChange={setBulkCompanyId}
                      emptyLabel="เลือกสาขา"
                    />
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={bulkCompanyId === NO_BRANCH_CHOSEN}
                    onClick={handleApplyBulk}
                  >
                    ใช้กับแถวที่เลือก (
                    {numberWithCommas(actions.branchRequired.length)})
                  </Button>
                </>
              )}

              <Button size="small" onClick={handleClearSelection}>
                ล้างการเลือก
              </Button>
            </Stack>
          </Paper>
        )}

        <TableContainer>
          <Table
            size="small"
            sx={{
              tableLayout: "fixed",
              minWidth: TABLE_MIN_WIDTH,
              "& th": { whiteSpace: "nowrap" },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  padding="checkbox"
                  sx={{ width: CHECKBOX_COLUMN_WIDTH }}
                >
                  <Tooltip title="เลือกทั้งหมดที่กรองอยู่ (ทุกหน้า) · Shift+คลิกเพื่อเลือกเป็นช่วง">
                    <span>
                      <Checkbox
                        size="small"
                        checked={allFilteredSelected}
                        indeterminate={
                          selectedInFilterCount > 0 && !allFilteredSelected
                        }
                        disabled={filteredSelectableRowNos.length === 0}
                        onChange={handleToggleSelectAllFiltered}
                        inputProps={{
                          "aria-label": "เลือกทั้งหมดที่กรองอยู่",
                        }}
                      />
                    </span>
                  </Tooltip>
                </TableCell>
                {COLUMNS.map((column) => (
                  <TableCell
                    key={column.label}
                    align={column.align}
                    sx={{ width: column.width }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    align="center"
                    sx={{ py: 5 }}
                  >
                    <Typography color="text.secondary" fontWeight={500}>
                      ไม่พบแถวตามเงื่อนไขที่เลือก
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {pageRows.map((row) => (
                <PreviewRow
                  key={row.row_no}
                  row={row}
                  branches={branches}
                  chosenCompanyId={branchChoiceByRow[row.row_no] ?? ""}
                  onChooseBranch={onChooseBranch}
                  selected={selectedRowNos.has(row.row_no)}
                  onToggleSelect={handleToggleSelectRow}
                  pastIncluded={includedPastRowNos.has(row.row_no)}
                  onTogglePast={onTogglePastRow}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <CataloguePagination
          totalCount={filteredRows.length}
          page={page}
          setPage={setPage}
          limit={limit}
          setLimit={setLimit}
        />
      </CardContent>

      <Snackbar
        key={autoFillNote?.seq}
        open={Boolean(autoFillNote)}
        autoHideDuration={AUTO_FILL_NOTE_MS}
        onClose={handleCloseAutoFillNote}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="info" variant="filled" onClose={onDismissAutoFillNote}>
          เติมสาขาให้อีก {numberWithCommas(autoFillNote?.count ?? 0)} แถวที่
          store number เดียวกัน
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default PreviewRowsTable;
